// BookingView.tsx
import React, { useEffect, useRef } from "react";
import { useParams } from "react-router-dom";
import {
  FaCheck,
  FaPrint,
  FaUser,
  FaUserCircle,
  FaUsers,
} from "react-icons/fa";
import moment from "moment";
import { MdAssignmentAdd, MdPayment } from "react-icons/md";
import { IoIosMail, IoMdCall, IoMdPricetags } from "react-icons/io";
import { FaPersonCirclePlus } from "react-icons/fa6";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { formatIndianNumber } from "../../_helper/helper";
import { getBookingDetailsByBookingNo } from "./services/booking.services";
import { clearBookingDetails } from "./bookingSlice";
import { LoadingPanel } from "../../components/layout/Loading";
import IconComponent from "../../components/common/IconComponent";
import { Button } from "@progress/kendo-react-buttons";
import NoDataFoundPage from "../../components/common/NoDataFoundPage";
import { LuNotebookPen } from "react-icons/lu";

const BookingView: React.FC = () => {
  const gridRef = useRef<any>(null);
  const dispatch = useAppDispatch();
  const params = useParams();
  const BookingNo = params?.BookingNo;
  const loading = useAppSelector((state) => state.booking.loading);
  const BookingViewDetails = useAppSelector(
    (state) => state.booking.BookingViewDetails,
  );

  useEffect(() => {
    if (BookingNo) {
      dispatch(getBookingDetailsByBookingNo(BookingNo));
    }
  }, [BookingNo]);

  useEffect(() => {
    return () => {
      dispatch(clearBookingDetails());
    };
  }, []);

  const getBookingStatus = (travelDate: string | Date) => {
    const today = new Date();
    const travel = new Date(travelDate);
    today.setHours(0, 0, 0, 0);
    travel.setHours(0, 0, 0, 0);
    if (today > travel) {
      return "Completed";
    }
    return "Confirmed";
  };

  const status = getBookingStatus(
    BookingViewDetails?.TravelDate
      ? moment(BookingViewDetails?.TravelDate, "DD/MM/YYYY").format(
          "YYYY-MM-DD",
        )
      : "",
  );

  // In BookingView.tsx, add this function to get ordered traveler sections
  const getOrderedTravellerSections = () => {
    const tourCost = BookingViewDetails || {};

    const allSections = [
      {
        key: "ExtraAdult",
        sequence: tourCost?.ExtraAdultSequenceNo,
        show: tourCost?.ExtraAdultRate && +tourCost?.ExtraAdultRate > 0,
        title: tourCost?.ExtraAdultTitle || "One Adult",
        description: tourCost?.ExtraAdultDescription,
        rate: tourCost?.ExtraAdultRate,
        pax: tourCost?.ExtraAdultPax || 0,
      },
      {
        key: "TwinSharing",
        sequence: tourCost?.TwinSharingSequenceNo,
        show: tourCost?.TwinSharingRate && +tourCost?.TwinSharingRate > 0,
        title: tourCost?.TwinSharingTitle || "Twin Sharing",
        description: tourCost?.TwinSharingDescription,
        rate: tourCost?.TwinSharingRate,
        pax: tourCost?.TwinSharingPax || 0,
      },
      {
        key: "ThreeSharing",
        sequence: tourCost?.ThreeSharingSequenceNo,
        show: tourCost?.ThreeSharingRate && +tourCost?.ThreeSharingRate > 0,
        title: tourCost?.ThreeSharingTitle || "Three Sharing",
        description: tourCost?.ThreeSharingDescription,
        rate: tourCost?.ThreeSharingRate,
        pax: tourCost?.ThreeSharingPax || 0,
      },
      {
        key: "FourSharing",
        sequence: tourCost?.FourSharingSequenceNo,
        show: tourCost?.FourSharingRate && +tourCost?.FourSharingRate > 0,
        title: tourCost?.FourSharingTitle || "Four Sharing",
        description: tourCost?.FourSharingDescription,
        rate: tourCost?.FourSharingRate,
        pax: tourCost?.FourSharingPax || 0,
      },
      {
        key: "CWB",
        sequence: tourCost?.CWBSequenceNo,
        show: tourCost?.CWBRate && +tourCost?.CWBRate > 0,
        title: tourCost?.CWBTitle || "Child With Bed",
        description: tourCost?.CWBDescription,
        rate: tourCost?.CWBRate,
        pax: tourCost?.CWBPax || 0,
      },
      {
        key: "CNB",
        sequence: tourCost?.CNBSequenceNo,
        show: tourCost?.CNBRate && +tourCost?.CNBRate > 0,
        title: tourCost?.CNBTitle || "Child With No Bed",
        description: tourCost?.CNBDescription,
        rate: tourCost?.CNBRate,
        pax: tourCost?.CNBPax || 0,
      },
    ];

    const visibleSections = allSections.filter((section) => section.show);

    return visibleSections.sort((a, b) => {
      const aSeq =
        a.sequence !== null && a.sequence !== undefined ? +a.sequence : 999;
      const bSeq =
        b.sequence !== null && b.sequence !== undefined ? +b.sequence : 999;

      if (aSeq !== bSeq) {
        return aSeq - bSeq;
      }
      return 0;
    });
  };

  if (loading) return <LoadingPanel gridRef={gridRef} />;
  return (
    <>
      {BookingViewDetails?.ID ? (
        <>
          <div className="container booking-view">
            {/* Main Content Section */}
            <section className="content-section">
              {/* Header Strip */}
              <div className="header-strip d-flex flex-column flex-md-row justify-content-md-between align-items-start gap-4">
                <div className="booking-info">
                  <h2 className="mb-2">
                    Booking No. :{" "}
                    <span className="font-semibold">
                      {BookingViewDetails?.BookingNo}
                    </span>
                  </h2>
                  <p>
                    Booked On :{" "}
                    <span className="font-semibold">
                      {BookingViewDetails?.CreatedOn
                        ? moment(
                            BookingViewDetails?.CreatedOn,
                            "DD/MM/YYYY",
                          ).format("DD MMM, YYYY")
                        : ""}
                    </span>
                  </p>
                </div>
                <div className="status-badge">
                  <div className="status-icon">
                    <IconComponent icon={FaCheck} className="w-6 h-6" />
                  </div>
                  <div>
                    <p className="text-sm mb-1">Booking Status</p>
                    <p className="font-medium">{status}</p>
                  </div>
                </div>
              </div>

              {/* Main Content */}
              <div className="main-content">
                {/* Tour Info */}
                <div className="tour-info-card mb-4">
                  <h2 className="mb-2">{BookingViewDetails?.TourName}</h2>
                  <p>
                    Travel Date :{" "}
                    <span className="font-semibold">
                      {BookingViewDetails?.TravelDate
                        ? moment(
                            BookingViewDetails?.TravelDate,
                            "DD/MM/YYYY",
                          ).format("DD MMM, YYYY")
                        : ""}
                    </span>
                  </p>
                </div>

                {/* Guest Information */}
                <div className="mt-5">
                  <div className="section-title">
                    <IconComponent icon={FaUserCircle} className="w-6 h-6" />
                    <span>Guest Information</span>
                  </div>
                  <div className="row g-4">
                    <div className="col-12 col-sm-6 col-lg-4">
                      <div className="info-card">
                        <div className="card-icon">
                          <IconComponent icon={FaUser} className="w-6 h-6" />
                        </div>
                        <div className="card-content">
                          <p className="text-muted">Full Name</p>
                          <p className="font-medium">
                            {BookingViewDetails?.FirstName}{" "}
                            {BookingViewDetails?.LastName}
                          </p>
                        </div>
                      </div>
                    </div>
                    <div className="col-12 col-sm-6 col-lg-4">
                      <div className="info-card">
                        <div className="card-icon">
                          <IconComponent icon={IoMdCall} className="w-6 h-6" />
                        </div>
                        <div className="card-content">
                          <p className="text-muted">Mobile No.</p>
                          <p className="font-medium">
                            {BookingViewDetails?.MobileNo}
                          </p>
                        </div>
                      </div>
                    </div>
                    <div className="col-12 col-sm-6 col-lg-4">
                      <div className="info-card">
                        <div className="card-icon">
                          <IconComponent icon={IoIosMail} className="w-6 h-6" />
                        </div>
                        <div className="card-content">
                          <p className="text-muted">Email ID</p>
                          <p className="font-medium word-break">
                            {BookingViewDetails?.EmailID}
                          </p>
                        </div>
                      </div>
                    </div>
                    <div className="col-12 col-sm-6 col-lg-4">
                      <div className="info-card">
                        <div className="card-icon">
                          <IconComponent
                            icon={FaPersonCirclePlus}
                            className="w-6 h-6"
                          />
                        </div>
                        <div className="card-content">
                          <p className="text-muted">Total Passengers</p>
                          <p className="font-medium">
                            {BookingViewDetails?.TotalPax || 0} Pax
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="mt-5">
                  <div className="section-title">
                    <IconComponent icon={FaUsers} className="w-6 h-6" />
                    <span>Traveller Summary</span>
                  </div>
                  <div className="row g-4">
                    <div className="col-12 ">
                      <div className="info-card">
                        <table className="w-100 traveller-table">
                          <thead>
                            <tr className="text-center">
                              <th
                                style={{ width: "50%", minWidth: 200 }}
                                className="text-start"
                              >
                                Particular
                              </th>
                              <th style={{ minWidth: 100 }}>No. Of Pax</th>
                              <th style={{ minWidth: 150 }}>Per Rate</th>
                              <th style={{ minWidth: 150 }}>Total Rate</th>
                            </tr>
                            <tr>
                              <th colSpan={4}>
                                <hr className="mt-2 mb-2" />
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            {getOrderedTravellerSections()?.map(
                              (section: any, index: number) => (
                                <>
                                  <tr key={section.key}>
                                    <td>
                                      <div>
                                        <p className="font-medium m-0">
                                          {section.title}
                                        </p>
                                        {section.description && (
                                          <p className="text-muted-foreground text-sm m-0">
                                            {section.description}
                                          </p>
                                        )}
                                      </div>
                                    </td>
                                    <td className="text-center">
                                      {section.pax || 0}
                                    </td>
                                    <td className="text-end">
                                      ₹ {formatIndianNumber(+section.rate || 0)}
                                    </td>
                                    <td className="text-end">
                                      <span className="font-semibold text-xl text-primary">
                                        ₹{" "}
                                        {formatIndianNumber(
                                          +(section.pax || 0) *
                                            +(section.rate || 0),
                                        )}
                                      </span>
                                    </td>
                                  </tr>
                                  {index + 1 !==
                                    getOrderedTravellerSections()?.length && (
                                    <tr>
                                      <td colSpan={4}>
                                        <hr className="mt-2 mb-2" />
                                      </td>
                                    </tr>
                                  )}
                                </>
                              ),
                            )}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Selected Add-ons */}
                {BookingViewDetails?.bookingaddons &&
                  BookingViewDetails?.bookingaddons?.length > 0 && (
                    <div className="mt-5" style={{ pageBreakInside: "avoid" }}>
                      <div className="section-title">
                        <IconComponent
                          icon={MdAssignmentAdd}
                          className="w-6 h-6"
                        />
                        <span>Selected Add-ons</span>
                      </div>
                      <div className="row g-4">
                        <div className="col-12 ">
                          <div className="info-card">
                            <table className="w-100 traveller-table">
                              <thead>
                                <tr className="text-center">
                                  <th
                                    style={{ width: "50%", minWidth: 200 }}
                                    className="text-start"
                                  >
                                    Particular
                                  </th>
                                  <th style={{ minWidth: 100 }}>No. Of Pax</th>
                                  <th style={{ minWidth: 150 }}>Per Rate</th>
                                  <th style={{ minWidth: 150 }}>Total Rate</th>
                                </tr>
                                <tr>
                                  <th colSpan={4}>
                                    <hr className="mt-2 mb-2" />
                                  </th>
                                </tr>
                              </thead>
                              <tbody>
                                {BookingViewDetails?.bookingaddons?.map(
                                  (addons: any, index: number) => (
                                    <>
                                      <tr key={index}>
                                        <td>
                                          <div>
                                            <p className="font-medium m-0">
                                              {addons?.Title}
                                            </p>
                                            {addons?.ShortDescription && (
                                              <p className="text-muted-foreground text-sm m-0">
                                                {addons?.ShortDescription}
                                              </p>
                                            )}
                                          </div>
                                        </td>
                                        <td className="text-center">
                                          {addons?.Quantity || 0}
                                        </td>
                                        <td className="text-end">
                                          ₹{" "}
                                          {formatIndianNumber(
                                            +addons?.Amount || 0,
                                          )}
                                        </td>
                                        <td className="text-end">
                                          <span className="font-semibold text-xl text-primary">
                                            ₹{" "}
                                            {formatIndianNumber(
                                              +(addons?.Quantity || 0) *
                                                +(addons?.Amount || 0),
                                            )}
                                          </span>
                                        </td>
                                      </tr>
                                      {index + 1 !==
                                        BookingViewDetails?.bookingaddons
                                          ?.length && (
                                        <tr>
                                          <td colSpan={4}>
                                            <hr className="mt-2 mb-2" />
                                          </td>
                                        </tr>
                                      )}
                                    </>
                                  ),
                                )}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      </div>
                      {/* <div>
                        {BookingViewDetails?.bookingaddons?.map(
                          (addons: any, index: number) => (
                            <div key={index} className="addon-card">
                              <div>
                                <p className="font-medium">{addons?.Title}</p>
                                <p className="text-muted text-sm">
                                  Quantity : {addons?.Quantity || "-"}
                                </p>
                              </div>
                              <p className="font-semibold text-primary text-xl">
                                ₹ {formatIndianNumber(+addons?.Amount || 0)}
                              </p>
                            </div>
                          ),
                        )}
                      </div> */}
                    </div>
                  )}

                {/* Pricing Summary */}
                <div className="mt-5">
                  <div className="section-title">
                    <IconComponent icon={IoMdPricetags} className="w-6 h-6" />
                    <span>Pricing Summary</span>
                  </div>
                  <div className="info-card">
                    <table className="pricing-table">
                      <tbody>
                        <tr className="border-bottom">
                          <td className="text-muted pb-2 pt-3">Basic Amount</td>
                          <td className="text-right font-semibold">
                            ₹{" "}
                            {formatIndianNumber(
                              +BookingViewDetails?.BasicAmt || 0,
                            )}
                          </td>
                        </tr>
                        {BookingViewDetails?.bookingaddons &&
                        +BookingViewDetails?.bookingaddons?.length > 0 ? (
                          <>
                            <tr className="border-bottom">
                              <td className="text-muted pb-2 pt-3">
                                Total Addons
                              </td>
                              <td className="text-right font-semibold">
                                ₹{" "}
                                {formatIndianNumber(
                                  (+BookingViewDetails?.TotalAmt || 0) -
                                    (+BookingViewDetails?.BasicAmt || 0),
                                )}
                              </td>
                            </tr>
                            <tr className="border-bottom">
                              <td className="font-medium pb-2 pt-3">
                                Total Amount
                              </td>
                              <td className="text-right font-semibold">
                                ₹{" "}
                                {formatIndianNumber(
                                  +BookingViewDetails?.TotalAmt || 0,
                                )}
                              </td>
                            </tr>
                          </>
                        ) : null}

                        {(BookingViewDetails?.CouponCodeValue &&
                          +BookingViewDetails?.CouponCodeValue > 0) ||
                        (BookingViewDetails?.DiscountAmt &&
                          +BookingViewDetails?.DiscountAmt > 0) ? (
                          <>
                            <tr>
                              <td className="text-muted pb-1" colSpan={2}>
                                Discount Applied
                              </td>
                            </tr>
                            <tr className="border-bottom">
                              <td className="pb-2 pt-0">
                                {BookingViewDetails?.CouponCodeValue &&
                                +BookingViewDetails?.CouponCodeValue > 0 ? (
                                  <p className="text-success pt-0 mb-0">
                                    Promocode ({BookingViewDetails?.CouponCode})
                                    -{" "}
                                    <b>
                                      ( ₹{" "}
                                      {formatIndianNumber(
                                        +BookingViewDetails?.CouponCodeValue ||
                                          0,
                                      )}
                                      )
                                    </b>
                                  </p>
                                ) : null}
                                {BookingViewDetails?.DiscountAmt &&
                                +BookingViewDetails?.DiscountAmt > 0 ? (
                                  <p className="text-success pt-0 mb-0">
                                    Discount Given -{" "}
                                    <b>
                                      ( ₹{" "}
                                      {formatIndianNumber(
                                        +BookingViewDetails?.DiscountAmt || 0,
                                      )}
                                      )
                                    </b>
                                  </p>
                                ) : null}
                              </td>
                              <td className="text-right font-semibold text-success pt-0">
                                - ₹{" "}
                                {formatIndianNumber(
                                  +(BookingViewDetails?.CouponCodeValue || 0) +
                                    +(BookingViewDetails?.DiscountAmt || 0),
                                )}
                              </td>
                            </tr>
                          </>
                        ) : null}
                        <tr className="border-bottom">
                          <td className="text-muted pb-2 pt-3">Paid Amount</td>
                          <td className="text-right font-semibold">
                            - ₹{" "}
                            {formatIndianNumber(
                              +BookingViewDetails?.PaymentAmount || 0,
                            )}
                          </td>
                        </tr>
                        <tr>
                          <td className="font-medium text-xl pb-2 pt-3 border-top">
                            Remaining Amount
                          </td>
                          <td className="text-right text-xl font-semibold text-primary border-top">
                            ₹{" "}
                            {formatIndianNumber(
                              (+BookingViewDetails?.TotalAmt || 0) -
                                (+BookingViewDetails?.PaymentAmount || 0) -
                                (+BookingViewDetails?.CouponCodeValue || 0) -
                                (+BookingViewDetails?.DiscountAmt || 0),
                            )}
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Payment Transaction Details */}
                {BookingViewDetails?.PaymentID && (
                  <div className="mt-5">
                    <div className="section-title">
                      <IconComponent icon={MdPayment} className="w-6 h-6" />
                      <span>Payment Transaction Details</span>
                    </div>
                    <div className="info-card">
                      <table className="pricing-table">
                        <tbody>
                          <tr className="border-bottom">
                            <td className="text-muted pb-2 pt-3">Payment ID</td>
                            <td className="text-right font-semibold">
                              {BookingViewDetails?.PaymentID}
                            </td>
                          </tr>
                          <tr className="border-bottom">
                            <td className="text-muted pb-2 pt-3">
                              Payment Method
                            </td>
                            <td className="text-right font-semibold text-capitalize">
                              {BookingViewDetails?.Method || "N/A"}
                              {BookingViewDetails?.Bank &&
                                ` (${BookingViewDetails?.Bank})`}
                            </td>
                          </tr>
                          <tr className="border-bottom">
                            <td className="text-muted pb-2 pt-3">
                              Transaction Date
                            </td>
                            <td className="text-right font-semibold">
                              {BookingViewDetails?.CreatedAt
                                ? moment
                                    .unix(BookingViewDetails?.CreatedAt)
                                    .format("DD MMM, YYYY hh:mm A")
                                : "N/A"}
                            </td>
                          </tr>
                          <tr className="border-bottom">
                            <td className="text-muted pb-2 pt-3">
                              Amount Paid
                            </td>
                            <td className="text-right font-semibold">
                              ₹{" "}
                              {formatIndianNumber(
                                +BookingViewDetails?.PaymentAmount || 0,
                              )}
                            </td>
                          </tr>
                          <tr>
                            <td className="text-muted pb-2 pt-3">
                              Transaction Description
                            </td>
                            <td className="text-right font-semibold">
                              {BookingViewDetails?.Description || "N/A"}
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
                {BookingViewDetails?.Method === "cash" &&
                  +BookingViewDetails?.PaymentAmount > 0 && (
                    <div className="mt-5">
                      <div className="section-title">
                        <IconComponent icon={MdPayment} className="w-6 h-6" />
                        <span>Payment Transaction Details</span>
                      </div>
                      <div className="info-card">
                        <table className="pricing-table">
                          <tbody>
                            <tr className="border-bottom">
                              <td className="text-muted pb-2 pt-3">
                                Payment Method
                              </td>
                              <td className="text-right font-semibold text-capitalize">
                                {BookingViewDetails?.Method || "N/A"}
                              </td>
                            </tr>
                            {BookingViewDetails?.PaymentDate && (
                              <tr className="border-bottom">
                                <td className="text-muted pb-2 pt-3">
                                  Payment Date
                                </td>
                                <td className="text-right font-semibold">
                                  {BookingViewDetails?.PaymentDate
                                    ? moment(
                                        BookingViewDetails?.PaymentDate,
                                        "DD/MM/YYYY",
                                      ).format("DD MMM, YYYY")
                                    : ""}
                                </td>
                              </tr>
                            )}
                            <tr className="border-bottom">
                              <td className="text-muted pb-2 pt-3">
                                Amount Paid
                              </td>
                              <td className="text-right font-semibold">
                                ₹{" "}
                                {formatIndianNumber(
                                  +BookingViewDetails?.PaymentAmount || 0,
                                )}
                              </td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}

                {/* Remarks  */}
                {BookingViewDetails?.Remarks && (
                  <div className="mt-5">
                    <div className="section-title">
                      <IconComponent icon={LuNotebookPen} className="w-6 h-6" />
                      <span>Remarks</span>
                    </div>
                    <div className="row g-4">
                      <div className="col-12 ">
                        <div className="info-card">
                          <p
                            className="mb-0"
                            dangerouslySetInnerHTML={{
                              __html: BookingViewDetails?.Remarks,
                            }}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </section>
          </div>
          <div className="container">
            <div className="row">
              <div className="col-12">
                <div className="d-flex justify-content-center printbuttons m-4">
                  <Button
                    themeColor={"primary"}
                    fillMode={"solid"}
                    className="d-flex align-items-center gap-2 print-button"
                    onClick={() => window.print()}
                  >
                    <IconComponent icon={FaPrint} /> Print
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </>
      ) : (
        <NoDataFoundPage />
      )}
    </>
  );
};

export default BookingView;
